import React, { useState, useEffect } from 'react';
import {
  // Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  // ListItemSecondaryAction,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Alert,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayCircle as ProcessingIcon,
  Queue as QueueIcon
} from '@mui/icons-material';
import axios from 'axios';

interface TaskStatus {
  task_id: string;
  type: string;
  status: string;
  priority: number;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  worker_id?: string;
  attempts: number;
  metadata?: any;
}

interface QueueStats {
  queues: {
    [key: string]: number;
  };
  workers: {
    active: number;
    details: Array<{
      worker_id: string;
      status: string;
      last_heartbeat: string;
      tasks_completed: number;
      tasks_failed: number;
    }>;
  };
  tasks: {
    [key: string]: number;
  };
}

interface Props {
  planId?: number;
  showCompact?: boolean;
}

const TaskQueueStatus: React.FC<Props> = ({ planId, showCompact = false }) => {
  const [tasks, setTasks] = useState<TaskStatus[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  // const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskStatus | null>(null);

  const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';

  useEffect(() => {
    loadTasks();
    loadStats();
    const interval = setInterval(() => {
      loadTasks();
      loadStats();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [planId]);

  const loadTasks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/task-queue/my-tasks`, {
        params: { limit: 10 }
      });
      setTasks(response.data);
    } catch (_error) {
      console.error('Failed to load tasks:', _error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/task-queue/stats`);
      setStats(response.data);
    } catch (_error) {
      console.error('Failed to load stats:', _error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon color="action" />;
      case 'processing':
        return <ProcessingIcon color="primary" />;
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <QueueIcon />;
    }
  };

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'processing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 0:
        return 'Low';
      case 1:
        return 'Normal';
      case 2:
        return 'High';
      case 3:
        return 'Urgent';
      default:
        return 'Unknown';
    }
  };

  const handleCancelTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to cancel this task?')) {
      try {
        await axios.post(`${API_BASE_URL}/task-queue/cancel/${taskId}`);
        loadTasks();
      } catch (_error) {
        alert('Failed to cancel task');
      }
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (showCompact) {
    const activeTasks = tasks.filter(t => ['pending', 'processing'].includes(t.status));
    
    if (activeTasks.length === 0 && !stats?.queues.processing) {
      return null;
    }

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <QueueIcon color="primary" />
              <Typography variant="subtitle1">
                Active Tasks: {activeTasks.length}
              </Typography>
              {stats && (
                <>
                  <Chip 
                    label={`Queue: ${Object.values(stats.queues).reduce((a, b) => a + b, 0)}`} 
                    size="small" 
                  />
                  <Chip 
                    label={`Workers: ${stats.workers.active}`} 
                    size="small" 
                    color="success"
                  />
                </>
              )}
            </Stack>
            <Button size="small" onClick={() => setShowDetails(true)}>
              View Details
            </Button>
          </Stack>
          {activeTasks.some(t => t.status === 'processing') && (
            <LinearProgress sx={{ mt: 1 }} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Task Queue Status</Typography>
            <IconButton onClick={() => { loadTasks(); loadStats(); }}>
              <RefreshIcon />
            </IconButton>
          </Stack>

          {stats && (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip label={`Pending: ${stats.queues.NORMAL || 0}`} />
              <Chip label={`Processing: ${stats.queues.processing || 0}`} color="primary" />
              <Chip label={`Completed: ${stats.tasks.tasks_completed || 0}`} color="success" />
              <Chip label={`Failed: ${stats.tasks.tasks_failed || 0}`} color="error" />
            </Stack>
          )}

          <List>
            {tasks.map((task) => (
              <ListItem 
                key={task.task_id}
                secondaryAction={
                  ['pending', 'processing'].includes(task.status) && (
                    <Tooltip title="Cancel task">
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelTask(task.task_id);
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )
                }
                disablePadding
              >
                <ListItemButton onClick={() => setSelectedTask(task)}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getStatusIcon(task.status)}
                        <Typography>{task.type}</Typography>
                        <Chip 
                          label={task.status} 
                          size="small" 
                          color={getStatusColor(task.status)}
                        />
                        <Chip 
                          label={getPriorityLabel(task.priority)} 
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatTime(task.created_at)}
                        {task.worker_id && ` | Worker: ${task.worker_id}`}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {tasks.length === 0 && (
            <Alert severity="info">No tasks in queue</Alert>
          )}
        </CardContent>
      </Card>

      {/* Task Details Dialog */}
      <Dialog 
        open={!!selectedTask} 
        onClose={() => setSelectedTask(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Task Details</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Task ID</TableCell>
                    <TableCell>{selectedTask.task_id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>{selectedTask.type}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <Chip 
                        label={selectedTask.status} 
                        color={getStatusColor(selectedTask.status)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Priority</TableCell>
                    <TableCell>{getPriorityLabel(selectedTask.priority)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Attempts</TableCell>
                    <TableCell>{selectedTask.attempts}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Created</TableCell>
                    <TableCell>{formatTime(selectedTask.created_at)}</TableCell>
                  </TableRow>
                  {selectedTask.started_at && (
                    <TableRow>
                      <TableCell>Started</TableCell>
                      <TableCell>{formatTime(selectedTask.started_at)}</TableCell>
                    </TableRow>
                  )}
                  {selectedTask.completed_at && (
                    <TableRow>
                      <TableCell>Completed</TableCell>
                      <TableCell>{formatTime(selectedTask.completed_at)}</TableCell>
                    </TableRow>
                  )}
                  {selectedTask.worker_id && (
                    <TableRow>
                      <TableCell>Worker</TableCell>
                      <TableCell>{selectedTask.worker_id}</TableCell>
                    </TableRow>
                  )}
                  {selectedTask.metadata && (
                    <TableRow>
                      <TableCell>Metadata</TableCell>
                      <TableCell>
                        <pre style={{ margin: 0 }}>
                          {JSON.stringify(selectedTask.metadata, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Details Dialog */}
      <Dialog 
        open={showDetails} 
        onClose={() => setShowDetails(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Task Queue Details</DialogTitle>
        <DialogContent>
          <TaskQueueStatus showCompact={false} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskQueueStatus;